import {
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  ReconStateEnum,
  filterByCellValueMenuItem,
  getRowNodesToOperateOn,
  includesEnumValue,
  selectAll,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useMixpanel,
  type FilterableProperty,
  type ReconMismatch,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-enterprise';
import { compact } from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import { useCurrentAppLayoutId } from '../../../../hooks/useAppLayoutId';
import { useResolveReconMismatchesDrawer } from '../../../../hooks/useResolveReconMismatchesDrawer';
import { getReconDetailsRoute } from '../../../Routes/routes';
import { colIDToFilterBuilderKey } from './useReconMismatchesFilter';

const RESOLVABLE_STATES = [ReconStateEnum.PendingReview];

export const useResolveMismatchMenu = ({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}) => {
  const mixpanel = useMixpanel();
  const { openResolveReconMismatchesDrawer } = useResolveReconMismatchesDrawer();
  const navigate = useNavigate();
  const layoutId = useCurrentAppLayoutId();

  const getMenuItems = useCallbackRef((params: GetContextMenuItemsParams<ReconMismatch>) => {
    const selectedMismatches = compact(getRowNodesToOperateOn(params).map(node => node.data));
    const resolvableSelectedMismatches = selectedMismatches.filter(mismatch =>
      includesEnumValue(RESOLVABLE_STATES, mismatch.State)
    );

    return compact([
      selectedMismatches.length > 0
        ? {
            name: `Open Details (${selectedMismatches.length})`,
            action: () => {
              const mismatchIDs = selectedMismatches.map(m => m.ID);
              navigate(getReconDetailsRoute({ mismatchIDs, layoutId }));
              mixpanel.track(MixpanelEvent.CreateTempReconMismatchDetailsTab, {
                [MixpanelEventProperty.Amount]: mismatchIDs.length,
              });
            },
            icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
          }
        : undefined,
      resolvableSelectedMismatches.length > 0
        ? {
            name: `Resolve (${resolvableSelectedMismatches.length})`,
            action: () => {
              openResolveReconMismatchesDrawer(
                resolvableSelectedMismatches.map(m => m.ID),
                MixpanelEventSource.ReconMismatchesBlotter
              );
            },
            icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
          }
        : undefined,
    ]);
  });

  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams) => {
    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      ...getMenuItems(params),
      'separator',
      selectAll(params, mixpanel),
      'separator',
      ...getDefaultContextMenuItems(params),
    ]);
  });

  return {
    getContextMenuItems,
  };
};
